/**
 * add-application.ts
 *
 * Shared portal-user association card behaviour for the Add Application form.
 * Used by both the School Portal and the Org Portal.
 *
 * - 300 ms debounce on blur of #GuardianEmail and #GuardianPhone
 * - When both fields are filled, email is tried first; if it yields no match the
 *   phone lookup runs as a fallback. This means an email match is always preferred
 *   over a phone match, but a phone match is still surfaced when email has no result.
 * - Reveals #portal-user-match-card (removes "d-none") when a match is found
 * - Hides the card when neither field matches or both are empty
 * - On toggle (form-check form-switch) checked: sets #linkedUserIdInput.value to the returned userId
 * - On toggle unchecked: clears #linkedUserIdInput.value
 * - On field input event after prior acceptance: clears linkedUserId and re-runs debounced lookup
 *
 * The endpoint URL is read from the form's `data-find-portal-user-url` attribute,
 * which is rendered by each portal's Razor view.
 */

const DEBOUNCE_MS = 300;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingController: AbortController | null = null;
/** The userId returned by the last successful AJAX match (cleared on field edit). */
let storedUserId: string | null = null;

// ── DOM references ─────────────────────────────────────────────────────────

function getForm(): HTMLFormElement | null {
  return document.querySelector('form[data-find-portal-user-url]') as HTMLFormElement | null;
}

function getEmailInput(): HTMLInputElement | null {
  return document.getElementById('GuardianEmail') as HTMLInputElement | null;
}

function getPhoneInput(): HTMLInputElement | null {
  return document.getElementById('GuardianPhone') as HTMLInputElement | null;
}

function getMatchCard(): HTMLElement | null {
  return document.getElementById('portal-user-match-card');
}

function getToggle(): HTMLInputElement | null {
  return document.getElementById('associationToggle') as HTMLInputElement | null;
}

function getLinkedUserIdInput(): HTMLInputElement | null {
  return document.getElementById('linkedUserIdInput') as HTMLInputElement | null;
}

// ── Core match logic ──────────────────────────────────────────────────────

/**
 * Shows the association card and stores the matched userId.
 */
function showMatchCard(userId: string): void {
  storedUserId = userId;
  const card = getMatchCard();
  if (card) card.classList.remove('d-none');
}

/**
 * Hides the association card, clears stored userId, resets toggle and hidden field.
 */
function hideMatchCard(): void {
  storedUserId = null;
  const card = getMatchCard();
  if (card) card.classList.add('d-none');

  const toggle = getToggle();
  if (toggle) toggle.checked = false;

  const hiddenInput = getLinkedUserIdInput();
  if (hiddenInput) hiddenInput.value = '';
}

/**
 * Fires a single find-portal-user request and returns the matched userId,
 * or null when there is no match or the response is not OK.
 * Throws on AbortError so the caller can distinguish cancellation.
 */
async function fetchPortalUser(
  baseUrl: string,
  param: string,
  signal: AbortSignal,
): Promise<string | null> {
  const response = await fetch(`${baseUrl}?${param}`, {
    signal,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (!response.ok) return null;

  const data = await response.json() as { matched: boolean; userId?: string };
  return data.matched && data.userId ? data.userId : null;
}

/**
 * Calls the find-portal-user AJAX endpoint and updates the card.
 * Email is tried first; phone is used as a fallback when email is present
 * but yields no match. This way an email match is always preferred, but a
 * phone match is still surfaced when email doesn't resolve.
 * Uses AbortController to cancel previous in-flight requests.
 */
async function lookupPortalUser(): Promise<void> {
  // Cancel any previous in-flight request(s).
  if (pendingController) {
    pendingController.abort();
  }
  pendingController = new AbortController();
  const { signal } = pendingController;

  const email = getEmailInput()?.value.trim() ?? '';
  const phone = getPhoneInput()?.value.trim() ?? '';

  const form = getForm();
  const baseUrl = form?.dataset.findPortalUserUrl ?? 'find-portal-user';

  if (email.length === 0 && phone.length === 0) {
    hideMatchCard();
    return;
  }

  try {
    // 1. Try email when present.
    if (email.length > 0) {
      const userId = await fetchPortalUser(baseUrl, `email=${encodeURIComponent(email)}`, signal);
      if (userId !== null) {
        showMatchCard(userId);
        return;
      }
    }

    // 2. Fall back to phone — runs when email is absent OR email had no match.
    if (phone.length > 0) {
      const userId = await fetchPortalUser(baseUrl, `phone=${encodeURIComponent(phone)}`, signal);
      if (userId !== null) {
        showMatchCard(userId);
        return;
      }
    }

    hideMatchCard();
  } catch (err) {
    // Ignore AbortError — it fires whenever a new lookup cancels an in-flight request
    // and must not clear an already-accepted association.
    if (err instanceof DOMException && err.name === 'AbortError') return;
    // For genuine network errors also avoid clearing an accepted association.
    hideMatchCard();
  }
}

/**
 * Schedules a debounced portal-user lookup.
 */
function scheduleLookup(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void lookupPortalUser();
  }, DEBOUNCE_MS);
}

// ── Input / clear logic ────────────────────────────────────────────────────

/**
 * Called on "input" event of email/phone fields AFTER a prior match was accepted.
 * Clears the stored userId and re-schedules a fresh lookup.
 */
function onFieldInput(): void {
  // If the user previously accepted an association, clear it on edit.
  if (storedUserId !== null) {
    const hiddenInput = getLinkedUserIdInput();
    if (hiddenInput) hiddenInput.value = '';
    storedUserId = null;
  }
  scheduleLookup();
}

// ── Bootstrap ────────────────────────────────────────────────────────────

/**
 * Initialises the portal-user association card behaviour.
 * No-ops immediately when no form with `data-find-portal-user-url` is present,
 * so this function is safe to call on every page load.
 */
export function initAddApplication(): void {
  if (!document.querySelector('form[data-find-portal-user-url]')) return;

  const emailInput = getEmailInput();
  const phoneInput = getPhoneInput();
  const toggle = getToggle();

  // A form re-rendered after a validation error keeps the accepted link in the hidden field.
  // Show the card with the switch on, so the link is visible and can be turned off.
  const acceptedUserId = getLinkedUserIdInput()?.value ?? '';
  if (acceptedUserId.length > 0) {
    showMatchCard(acceptedUserId);
    if (toggle) toggle.checked = true;
  }

  // Trigger lookup on blur (field leave) with debounce.
  emailInput?.addEventListener('blur', scheduleLookup);
  phoneInput?.addEventListener('blur', scheduleLookup);

  // Re-schedule lookup (and clear prior acceptance) on input changes.
  emailInput?.addEventListener('input', onFieldInput);
  phoneInput?.addEventListener('input', onFieldInput);

  // Toggle: populate or clear the hidden LinkedUserId field.
  toggle?.addEventListener('change', () => {
    const hiddenInput = getLinkedUserIdInput();
    if (!hiddenInput) return;

    if (toggle.checked && storedUserId) {
      hiddenInput.value = storedUserId;
    } else {
      hiddenInput.value = '';
    }
  });
}
